// Libs
import classNames from 'classnames/bind';
import { Autocomplete, Box, FormHelperText, InputLabel, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ChangeEvent, useEffect, useState } from 'react';
// Components, Layouts, Pages
// Others
import { varColor, varTypography } from '~/themes/themeVariables';
import { IBaseOption } from '~/utils/interface/common';
import { EMPTY_STRING, ENTER_KEY, MAX_SELECTED_ITEM, SPACE } from '~/utils/constants/common';
import { BusinessPriceBookTypeEnum } from '~/utils/enum/common';
// Styles, images, icons
import styles from './BaseInputSelect.module.scss';
import { icons } from '~/assets';

type Props = {
  id?: string;
  label?: string;
  options: IBaseOption[];
  value?: string[];
  name?: string;
  maxItem?: number;
  width?: number | string;
  required?: boolean;
  placeholder?: string;
  isCheckEnter?: boolean;
  errorMessage?: string;
  onChange?: (value: string[], type?: string, name?: string) => void;
};

const cx = classNames.bind(styles);

const BaseInputSelect = (props: Props) => {
  //#region Destructuring Props
  const {
    id,
    label,
    options,
    required,
    errorMessage,
    value,
    isCheckEnter,
    name,
    maxItem = MAX_SELECTED_ITEM,
    onChange,
  } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['common/base']);
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [listOption, setListOption] = useState<IBaseOption[]>([]);
  const [selectedItem, setSelectedItem] = useState<IBaseOption[]>([]);
  const [valueSearch, setValueSearch] = useState<string>();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    if (!options) return;

    setListOption(options);
  }, [options]);

  useEffect(() => {
    if (!value || !options) return;
    const formattedValue = value.map((item) => item.toString());

    const matchedOptions: IBaseOption[] = options.filter((option) => formattedValue.includes(option.value));

    const unmatchedValues = formattedValue.filter((val) => !options.some((option) => option.value === val));

    const newOptions: IBaseOption[] = unmatchedValues.map((val) => ({
      label: val,
      value: val,
    }));

    const finalOptions = [...matchedOptions, ...newOptions];

    setSelectedItem(finalOptions);
    setListOption(
      options.filter((option) => !finalOptions.some((selected) => selected.value === option.value))
    );
  }, [value, options]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleDisplayOption = () => {
    setIsOpen(!isOpen);
  };

  const handleSelectedItem = (values: IBaseOption[], _event: React.SyntheticEvent, _details: string) => {
    if (values.length > maxItem) return;

    setSelectedItem(values);

    onChange?.(
      values.map((item) => item.value),
      BusinessPriceBookTypeEnum.ID,
      name || EMPTY_STRING
    );

    setListOption(() => {
      return options.filter((option) => !values.some((selected) => selected.value === option.value));
    });
  };

  const handleAddItem = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === SPACE) {
      event.stopPropagation();
    }

    if (event.key === ENTER_KEY) {
      event.preventDefault();

      if (selectedItem.length < maxItem && !isCheckEnter) {
        handleAddOption();
      }
    }
  };

  const handleAddOption = () => {
    if (!valueSearch?.trim()) return;

    setSelectedItem((prevSelectedItems) => {
      const isValueInListOption = listOption.find((item) => item.value === valueSearch);
      const isValueInSelectedItem = prevSelectedItems.some((item) => item.value === valueSearch);

      if (isValueInSelectedItem) return prevSelectedItems;

      const newItem: IBaseOption = isValueInListOption || { label: valueSearch, value: valueSearch };
      const updatedItems = [...prevSelectedItems, newItem];

      onChange?.(
        updatedItems.map((item) => item.value),
        BusinessPriceBookTypeEnum.NAME,
        name || EMPTY_STRING
      );

      return updatedItems;
    });
  };

  const handleSearchOption = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setValueSearch(value);
  };
  //#endregion Handle Function

  return (
    <Box component={'div'} id='baseInputSelectComponent' className={cx('container')}>
      {label && (
        <InputLabel required={required} className={cx('label')}>
          {label}
        </InputLabel>
      )}

      <Autocomplete
        id={id || 'autocomplete'}
        multiple
        open={isOpen}
        onOpen={handleDisplayOption}
        onClose={handleDisplayOption}
        disableClearable
        value={selectedItem}
        onChange={(event, newValue, details) => handleSelectedItem(newValue, event, details)}
        options={listOption}
        getOptionLabel={(option) => option?.label ?? EMPTY_STRING}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        disableCloseOnSelect
        onKeyDown={(event) => handleAddItem(event)}
        slotProps={{
          paper: {
            className: cx('paper'),
          },
          listbox: {
            className: cx('listBox'),
            sx: {
              '.MuiAutocomplete-option': {
                p: '8px',
                minHeight: '35px',
                borderRadius: '6px',
                fontSize: varTypography.fontSize['12'],
                color: varColor.secondary,
              },
            },
          },
          chip: {
            className: cx('chip'),
            sx: {
              height: '25px !important',
              '& .MuiChip-deleteIcon': {
                fontSize: `${varTypography.fontSize['16']} !important`,
              },
            },
          },
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            className={cx('textField')}
            sx={{
              '.MuiOutlinedInput-root.MuiInputBase-sizeSmall': {
                pl: '12px',
                minHeight: '35px',
              },
              '.MuiOutlinedInput-root.MuiInputBase-sizeSmall .MuiAutocomplete-input': {
                p: 0,
                minHeight: '30px',
              },
              '.MuiOutlinedInput-root': {
                fontSize: varTypography.fontSize['12'],
                minHeight: '30px',
                borderRadius: '9px',
                padding: '4px 8px',
                gap: '6px',
              },
              '.MuiAutocomplete-input': {
                color: varColor.textPlaceholderField,
              },
              '.MuiOutlinedInput-root .MuiAutocomplete-endAdornment': {
                right: '12px',
              },
            }}
            {...(selectedItem.length >= maxItem ? {} : { placeholder: t('common_search_placeholder') })}
            inputProps={{
              ...params.inputProps,
              readOnly: selectedItem.length >= maxItem,
            }}
            onChange={handleSearchOption}
          />
        )}
        popupIcon={
          <img src={icons.commonIconArrowDown} alt={t('common_alt_img')} className={cx('arrowIcon')} />
        }
        noOptionsText={<Typography className={cx('noOptions')}>{t('common_no_data_available')}</Typography>}
      />

      {errorMessage && <FormHelperText className={cx('errorMessage')}>{errorMessage}</FormHelperText>}
    </Box>
  );
};

export default BaseInputSelect;
